import { useTranslation } from 'react-i18next';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  delay?: number;
}

const FeatureCard = ({ icon, title, description, delay = 0 }: FeatureCardProps) => {
  return (
    <div 
      className="group relative p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-slate-700/50 backdrop-blur-sm hover:border-cyan-500/50 transition-all duration-500 hover:transform hover:scale-105"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      {/* Icon */}
      <div className="relative mb-6">
        <div className="w-16 h-16 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center group-hover:shadow-lg group-hover:shadow-cyan-500/25 transition-all duration-300">
          <i className={`${icon} text-2xl text-white`}></i>
        </div>
      </div>

      {/* Content */}
      <div className="relative">
        <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-400 transition-colors duration-300">
          {title}
        </h3>
        <p className="text-gray-300 leading-relaxed">
          {description}
        </p>
      </div>

      {/* Decorative Element */}
      <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-xl group-hover:from-cyan-500/20 group-hover:to-blue-500/20 transition-all duration-500"></div>
    </div>
  );
};

const Features = () => {
  const { t } = useTranslation('common');

  const features = [
    {
      icon: 'ri-coins-line',
      title: t('features.items.multiCurrency.title'),
      description: t('features.items.multiCurrency.description')
    },
    {
      icon: 'ri-links-line',
      title: t('features.items.multiChain.title'),
      description: t('features.items.multiChain.description')
    },
    {
      icon: 'ri-flashlight-line',
      title: t('features.items.quickIntegration.title'),
      description: t('features.items.quickIntegration.description')
    },
    {
      icon: 'ri-shield-check-line',
      title: t('features.items.security.title'),
      description: t('features.items.security.description')
    },
    {
      icon: 'ri-time-line',
      title: t('features.items.realtime.title'),
      description: t('features.items.realtime.description')
    },
    {
      icon: 'ri-customer-service-line',
      title: t('features.items.support.title'),
      description: t('features.items.support.description')
    }
  ];

  return (
    <section id="features" className="py-20 bg-slate-800 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.1),transparent_50%)]"></div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {t('features.title')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('features.subtitle')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;